/**
 * Startup Loading Overlay
 *
 * Full-screen overlay displayed during cache refresh on app startup.
 * Shows progress bar and real-time status messages.
 */

import React from 'react';
import { useCacheStore } from '@shared/store/cacheStore';

/**
 * Startup loading overlay component
 *
 * Automatically shown when cache is refreshing.
 * Displays progress bar and status message.
 *
 * @example
 * ```tsx
 * <StartupLoadingOverlay />
 * ```
 */
export const StartupLoadingOverlay: React.FC = () => {
  const { isRefreshing, progress, message } = useCacheStore();

  // Only show during refresh
  if (!isRefreshing) return null;

  return (
    <div className="fixed inset-0 bg-dark-bg/95 flex items-center justify-center z-50 backdrop-blur-sm">
      <div className="bg-dark-surface p-8 rounded-xl shadow-2xl border border-dark-border/30 max-w-md w-full">
        {/* Title */}
        <h2 className="text-xl font-semibold text-white mb-2">
          Syncing Cache...
        </h2>

        {/* Subtitle */}
        <p className="text-sm text-dark-muted mb-6">
          Loading fresh data from Pipedrive. This may take a moment.
        </p>

        {/* Progress Bar */}
        <div className="w-full h-3 bg-dark-border rounded-full mb-2 overflow-hidden">
          <div
            className="h-full bg-brand-500 transition-all duration-200 ease-out"
            style={{ width: `${progress}%` }}
          />
        </div>

        {/* Status Message */}
        <p className="text-sm text-dark-muted mb-1">{message || 'Starting...'}</p>

        {/* Progress Percentage */}
        <p className="text-xs text-brand-500 font-semibold">{progress}% complete</p>

        {/* Spinner Icon (optional visual) */}
        {progress < 100 && (
          <div className="flex justify-center mt-4">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-brand-500"></div>
          </div>
        )}
      </div>
    </div>
  );
};
